import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { PayoutColumn, PayoutTabValues } from './types';

import type { LabelColor } from '@pflegenavi/web-components';
import {
  type ConfigType,
  Label,
  makeConfig,
  PaginatedTable2,
  Typography,
} from '@pflegenavi/web-components';
import { Stack } from '@mui/material';
import { YEAR_SHORTMONTH_DAY_FORMAT } from '@pflegenavi/shared/constants';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { IconTooltip } from '@pflegenavi/transaction-components';
import { useTheme } from '@mui/material/styles';
import type { GetPayoutsResult } from '@pflegenavi/service-provider-frontend/api';
import { PayoutReconciliationDialog } from './PayoutReconciliationDialog';

interface PayoutsTableProps {
  isLoading: boolean;
  payouts: GetPayoutsResult | undefined;
  tabFilterValue: PayoutTabValues;
  setTabFilterValue: (tab: PayoutTabValues) => void;
  totalCount: number;
  totalPages: number;
  rowsPerPage: number;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

interface PayoutsTableRow {
  id: string;
  initiationDate: Date;
  ibanLast4: string;
  amount: number;
  statusColor: LabelColor;
  statusString: string;
}

type ColumnNames =
  | PayoutColumn.ibanLast4
  | PayoutColumn.initiationDate
  | PayoutColumn.amount
  | PayoutColumn.status
  | PayoutColumn.reconciliation;

type Filters = '';

export const PayoutsTable: FC<PayoutsTableProps> = ({
  isLoading,
  payouts,
  tabFilterValue,
  setTabFilterValue,
  totalCount,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
}) => {
  const { t } = useTranslation();
  const fDate = useFormatDate();
  const { fCurrencyCents } = useFormatting();

  const rows = useMemo<PayoutsTableRow[]>(
    () =>
      payouts?.data?.map((payout) => {
        const statusColor =
          payout.state === 'CREATED'
            ? 'warning'
            : payout.state === 'SUCCEEDED'
            ? 'success'
            : ('error' as LabelColor);
        const statusString =
          payout.state === 'CREATED'
            ? 'pending'
            : payout.state === 'SUCCEEDED'
            ? 'completed'
            : 'failed';

        return {
          id: payout.id,
          initiationDate: new Date(payout.inserted_at),
          ibanLast4: payout.iban_last_4,
          amount: payout.amount,
          statusColor,
          statusString,
        };
      }) ?? [],
    [payouts]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage: number) => {
      setRowsPerPage(rowsPerPage);
      setPage(0);
    },
    [setPage, setRowsPerPage]
  );

  const config: ConfigType<PayoutsTableRow, ColumnNames, Filters> = useMemo(
    () =>
      makeConfig<PayoutsTableRow, ColumnNames, Filters>({
        showAll: undefined,
        onClickRow: undefined,
        hoverRow: true,
        tabs: {
          selectedItem: tabFilterValue,
          updateFilterModel: setTabFilterValue,
          items: [
            {
              value: PayoutTabValues.All,
              label: t('accounting.payouts.states.all'),
              color: 'default',
              count: undefined,
            },
            {
              value: PayoutTabValues.Paid,
              label: t('accounting.payouts.states.completed'),
              color: 'success',
              count: undefined,
            },
            {
              value: PayoutTabValues.Pending,
              label: t('accounting.payouts.states.pending'),
              color: 'warning',
              count: undefined,
            },
            {
              value: PayoutTabValues.Failed,
              label: t('accounting.payouts.states.failed'),
              color: 'error',
              count: undefined,
            },
          ],
        },
        columns: [
          {
            key: PayoutColumn.ibanLast4,
            label: t('accounting.payouts.table-head.4-digits'),
            align: 'left',
            component: ({ row: { ibanLast4 } }) => (
              <Typography variant="subtitle2" noWrap>
                **** {ibanLast4}
              </Typography>
            ),
          },
          {
            key: PayoutColumn.initiationDate,
            label: t('accounting.payouts.table-head.initiation-date'),
            align: 'left',
            component: ({ row: { initiationDate } }) => (
              <Typography variant="subtitle2" noWrap>
                {fDate(new Date(initiationDate), YEAR_SHORTMONTH_DAY_FORMAT)}
              </Typography>
            ),
          },
          {
            key: PayoutColumn.amount,
            label: t('accounting.payouts.table-head.amount'),
            align: 'right',
            minWidth: 100,
            maxWidth: 150,
            sx: { paddingRight: 8 },
            component: ({ row: { amount } }) => (
              <Stack sx={{ paddingRight: 6 }}>
                <Typography variant="subtitle2">
                  {fCurrencyCents(amount)}
                </Typography>
              </Stack>
            ),
          },
          {
            key: PayoutColumn.status,
            label: t('accounting.payouts.table-head.status'),
            align: 'left',
            component: ({ row: { statusColor, statusString } }) => (
              <Label color={statusColor} variant="ghost">
                {t(`accounting.payouts.states.${statusString}`)}
              </Label>
            ),
          },
          {
            key: PayoutColumn.reconciliation,
            label: t('accounting.payouts.table-head.reconciliation'),
            align: 'left',
            component: (params: { row: PayoutsTableRow }) => (
              <PayoutReconciliationDownload
                payoutId={params.row.id}
                initiationDate={params.row.initiationDate}
              />
            ),
          },
        ],
        emptyRowHeight: 77,
        pagination: {
          total: totalCount,
          page,
          onPageChange: setPage,
          pageSize: rowsPerPage,
          onPageSizeChange: handleChangeRowsPerPage,
          pageSizeOptions: [5, 10, 25, 50],
        },
      }),
    [
      tabFilterValue,
      setTabFilterValue,
      t,
      totalCount,
      page,
      setPage,
      rowsPerPage,
      handleChangeRowsPerPage,
      fDate,
      fCurrencyCents,
    ]
  );

  return (
    <PaginatedTable2
      name="AccountingPayoutsTable"
      isLoading={isLoading}
      config={config}
      rows={rows}
    />
  );
};

interface PayoutReconciliationDownloadProps {
  payoutId: string;
  initiationDate: Date;
}

const PayoutReconciliationDownload: FC<PayoutReconciliationDownloadProps> = ({
  payoutId,
  initiationDate,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <IconTooltip
        title={t('tooltip.download-reconciliation')}
        color={theme.palette.success.main}
        icon="check"
        onClick={() => setDialogOpen(true)}
      />
      <PayoutReconciliationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        payoutId={payoutId}
        initiationDate={initiationDate}
      />
    </>
  );
};
